// In-app Companies module — for Identium super-admins (admin role in the
// "default" tenant). Lives inside the regular tenant app (not /host) so
// Identium's day-to-day workflow stays in one place: they sign in once at
// /login as Identium's admin, and "Companies" appears as a sidebar item
// alongside Dashboard / All Assets / etc.
//
// All API calls go to /api/system/* using the user's regular tenant token
// (the requireHostOrSuperAdmin middleware on the backend recognises it).
// The standalone /host/login + /host/companies pages still work as a
// recovery fallback for the case where Identium has lost access to its
// tenant admin account.

import React, { useEffect, useState } from 'react';
import {
  MdBusiness, MdAdd, MdBlock, MdPlayArrow, MdArchive, MdDelete,
  MdLockReset, MdCloudDownload, MdContentCopy, MdRefresh, MdEdit, MdDriveFileRenameOutline,
  MdTune, MdSave, MdClose, MdBackup, MdRestore, MdHistory, MdUploadFile,
} from 'react-icons/md';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { confirm, prompt, alertBox } from '../lib/dialogs';
import SearchableSelect from '../components/SearchableSelect';

// Sort the tenants list so children sit directly under their parent.
//   • Platform owner first (always at the top — it's "the root" visually).
//   • Then for each top-level row (no parent_tenant_id), the row itself
//     followed by its children sorted by slug. Children of children are
//     not a thing today (only platform-owner can promote to reseller and
//     UI doesn't expose nesting under another reseller), but the recursion
//     handles arbitrary depth in case that changes later.
//   • Each row gets a `_depth` integer used for the indent prefix.
//
// Resellers receive only their direct children from the backend, so for
// them the result is effectively flat at depth 0 — same code path.
function orderAsTree(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const childrenOf = new Map();   // parentId|null -> [rows]
  for (const r of rows) {
    const k = r.parent_tenant_id || 'root';
    if (!childrenOf.has(k)) childrenOf.set(k, []);
    childrenOf.get(k).push(r);
  }
  for (const arr of childrenOf.values()) {
    arr.sort((a, b) => String(a.slug).localeCompare(String(b.slug)));
  }
  const out = [];
  const visit = (row, depth) => {
    out.push({ ...row, _depth: depth });
    const kids = childrenOf.get(row.id) || [];
    for (const k of kids) visit(k, depth + 1);
  };
  // Platform owner always at the top, then everything else at root level.
  const root = childrenOf.get('root') || [];
  const owner = root.find(r => r.is_platform_owner);
  const others = root.filter(r => !r.is_platform_owner);
  if (owner) visit(owner, 0);
  for (const r of others) visit(r, 0);
  return out;
}

export default function Companies() {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [showPlans, setShowPlans] = useState(false);
  const [showBackups, setShowBackups] = useState(null); // tenant or 'all' or null
  const [createResult, setCreateResult] = useState(null);
  const [resetResult, setResetResult]   = useState(null);
  const [error, setError] = useState('');
  // System-wide ceilings so the inputs render `max=` and matching helper
  // text. Cached on mount; small static payload from /api/system/limits.
  const [limits, setLimits] = useState({ maxUsers: 100000, maxAssets: 100000000 });

  // Identium super-admin (platform owner) can do everything.
  // Reseller admin can manage only their own children — they CAN access
  // this page (the backend filters the list to their children) but can't
  // see Plan templates / Backups across all companies / promote others
  // to reseller. Regular tenant admins see neither.
  const isSuperAdmin =
    user?.role === 'admin' && !!user?.tenant?.is_platform_owner;
  const isResellerAdmin =
    user?.role === 'admin' && !!user?.tenant?.is_reseller && !user?.tenant?.is_platform_owner;
  const canAccessCompanies = isSuperAdmin || isResellerAdmin;

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get('/system/tenants');
      // Tree-style ordering so children sit directly under their parent:
      //   1. platform owner first (Identium)
      //   2. then each top-level tenant (no parent), with its children right after
      //   3. children sorted alphabetically by slug within each parent group
      // The backend filters resellers down to their direct children, so for
      // them the tree is effectively flat — same code still works.
      setTenants(orderAsTree(r.data));
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (canAccessCompanies) {
      reload();
      api.get('/system/limits').then(r => setLimits(r.data || limits)).catch(() => {});
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessCompanies]);

  const suspend = async (t) => {
    if (!(await confirm(
      `Users will be logged out and login will be blocked until you resume.`,
      { title: `Suspend "${t.name}"?`, confirmLabel: 'Suspend', danger: true },
    ))) return;
    await api.post(`/system/tenants/${t.id}/suspend`); reload();
  };
  const resume = async (t) => { await api.post(`/system/tenants/${t.id}/resume`); reload(); };
  const archive = async (t) => {
    if (!(await confirm(
      `The DB file is preserved but the company is locked out. Reversible.`,
      { title: `Archive "${t.name}"?`, confirmLabel: 'Archive', danger: true },
    ))) return;
    await api.post(`/system/tenants/${t.id}/archive`); reload();
  };
  const hardDelete = async (t) => {
    if (!(await confirm(
      `This will PERMANENTLY DELETE "${t.name}" and its database file. This cannot be undone.`,
      { title: 'Permanent deletion', confirmLabel: 'Continue', danger: true },
    ))) return;
    const typed = await prompt(`Type "${t.slug}" to confirm.`, '', {
      title: 'Confirm slug',
      inputLabel: 'Slug',
      placeholder: t.slug,
      confirmLabel: 'Delete',
    });
    if (typed !== t.slug) {
      if (typed != null) await alertBox('Slug mismatch — deletion aborted.', { title: 'Aborted' });
      return;
    }
    try {
      await api.delete(`/system/tenants/${t.id}?confirm=${t.slug}`);
      reload();
    } catch (e) { await alertBox(e.response?.data?.error || 'Delete failed', { title: 'Delete failed' }); }
  };
  const renameSlug = async (t) => {
    const proposed = await prompt(
      `Lowercase letters, digits, and hyphens only (3–32 chars).\n\n` +
      `This will rename the DB file, rename the uploads directory, fix embedded logo / asset-image URLs, and log every user in this company out so they pick up the new tenant identity on next login.\n\n` +
      `Note: if you're renaming the company you're signed in as, YOU will be logged out too.`,
      t.slug,
      { title: `Rename "${t.slug}"`, inputLabel: 'New slug', confirmLabel: 'Rename' },
    );
    if (proposed == null || proposed === t.slug) return;
    const newSlug = proposed.toLowerCase().trim();
    try {
      const r = await api.post(`/system/tenants/${t.id}/rename`, { newSlug });
      await alertBox(r.data.message || `Renamed to "${newSlug}".`, { title: 'Rename complete' });
      reload();
    } catch (e) {
      await alertBox(e.response?.data?.error || 'Rename failed', { title: 'Rename failed' });
    }
  };
  const resetAdmin = async (t) => {
    const username = await prompt(
      `Which admin user in "${t.name}" should be reset?`,
      'admin',
      { title: 'Reset admin password', inputLabel: 'Username', confirmLabel: 'Reset' },
    );
    if (username == null) return;
    try {
      const r = await api.post(`/system/tenants/${t.id}/reset-admin`, { username });
      setResetResult({ tenant: t, ...r.data });
    } catch (e) { await alertBox(e.response?.data?.error || 'Reset failed', { title: 'Reset failed' }); }
  };
  const backup = async (t) => {
    const r = await api.get(`/system/tenants/${t.id}/backup`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement('a');
    a.href = url; a.download = `${t.slug}-${new Date().toISOString().slice(0,10)}.db`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
  };

  if (!canAccessCompanies) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div className="page-title"><MdBusiness /> Companies</div>
          <div className="page-subtitle">Super-admin or reseller access only</div>
        </div>
        <div className="card" style={{ padding: 24, color: 'var(--text-muted)' }}>
          You need to be signed in as a platform-owner super-admin or a reseller-tenant admin
          to manage companies. Regular tenant admins do not have access to this module.
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <div className="page-title"><MdBusiness /> Companies</div>
          <div className="page-subtitle">
            {isSuperAdmin
              ? 'Onboard, suspend, upgrade, and back up customer companies. Each company gets its own isolated database file.'
              : 'Manage the companies you onboard. Each company gets its own isolated database file.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={reload} className="btn btn-outline btn-sm"><MdRefresh /> Refresh</button>
          {isSuperAdmin && (
            <>
              <button onClick={() => setShowBackups('all')} className="btn btn-outline btn-sm"><MdHistory /> Backups</button>
              <button onClick={() => setShowPlans(true)} className="btn btn-outline btn-sm"><MdTune /> Manage plans</button>
            </>
          )}
          <button onClick={() => setShowCreate(true)} className="btn btn-primary"><MdAdd /> New company</button>
        </div>
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Slug</th>
                <th>Name</th>
                <th>Parent</th>
                <th>Plan</th>
                <th>Limits</th>
                <th>Status</th>
                <th>Counts</th>
                <th>Created</th>
                <th>Created by</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: 24 }}>Loading…</td></tr>
              ) : tenants.length === 0 ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: 24, color: '#888' }}>No companies onboarded yet</td></tr>
              ) : tenants.map(t => (
                <tr key={t.id}>
                  <td>
                    {/* Tree indent: child rows show "└─" so the hierarchy
                        reads at a glance without an actual <ul> structure. */}
                    {t._depth > 0 && (
                      <span style={{ display: 'inline-block', color: '#90a4ae', marginRight: 4, fontFamily: 'monospace' }}>
                        {'  '.repeat(t._depth - 1)}└─
                      </span>
                    )}
                    <code style={{ fontSize: 12, background: '#eef1f6', padding: '2px 6px', borderRadius: 4 }}>{t.slug}</code>
                    {!!t.is_platform_owner && (
                      <span title="Platform owner (Tag My Assets)" style={{
                        marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#fff',
                        background: '#6a1b9a', padding: '1px 6px', borderRadius: 4,
                      }}>{t.id === user?.tenant?.id ? 'YOU' : 'OWNER'}</span>
                    )}
                    {!!t.is_reseller && !t.is_platform_owner && (
                      <span title="Reseller — can onboard their own customer companies" style={{
                        marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#fff',
                        background: '#00838f', padding: '1px 6px', borderRadius: 4,
                      }}>{t.id === user?.tenant?.id ? 'YOU · RESELLER' : 'RESELLER'}</span>
                    )}
                  </td>
                  <td style={{ fontWeight: 600 }}>{t.name}</td>
                  <td style={{ fontSize: 12, color: '#666' }}>
                    {t.parent_name ? (
                      <span title={`Onboarded under reseller "${t.parent_slug}"`}>
                        {t.parent_name} <code style={{ fontSize: 10, color: '#90a4ae' }}>{t.parent_slug}</code>
                      </span>
                    ) : (
                      <span style={{ color: '#bbb' }}>—</span>
                    )}
                  </td>
                  <td><span className={`badge ${t.plan === 'enterprise' ? 'badge-success' : t.plan === 'trial' ? 'badge-warning' : 'badge-info'}`}>{t.plan}</span></td>
                  <td style={{ fontSize: 12, color: '#666' }}>
                    {t.max_users} users · {Number(t.max_assets).toLocaleString()} assets
                    {t.expires_at && <><br/>expires {new Date(t.expires_at).toLocaleDateString()}</>}
                  </td>
                  <td>
                    <span className={`badge ${t.status === 'active' ? 'badge-success' : t.status === 'suspended' ? 'badge-warning' : 'badge-secondary'}`}>
                      {t.status}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: '#666' }}>
                    {t.counts?.error
                      ? <span style={{ color: '#c62828' }}>{t.counts.error}</span>
                      : <>{t.counts?.users ?? 0} users · {t.counts?.assets ?? 0} assets</>
                    }
                  </td>
                  <td style={{ fontSize: 12, color: '#666' }}>{(t.created_at || '').slice(0, 10)}</td>
                  <td style={{ fontSize: 12, color: '#666' }}>
                    {t.created_by_label || <span style={{ color: '#bbb' }}>—</span>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <button className="btn btn-icon btn-sm btn-accent" title="Edit limits / plan / contact" onClick={() => setEditing(t)}><MdEdit /></button>
                      <button className="btn btn-icon btn-sm btn-outline" title="Rename slug (URL identifier)" onClick={() => renameSlug(t)}><MdDriveFileRenameOutline /></button>
                      {/* Suspend / archive / delete are NEVER offered for the
                          platform-owner row (Identium's own tenant). The
                          backend refuses these operations regardless, but
                          hiding the buttons stops accidental clicks and
                          confusing 400 errors. */}
                      {!t.is_platform_owner && t.status === 'active' && (
                        <button className="btn btn-icon btn-sm btn-outline" title="Suspend" onClick={() => suspend(t)}><MdBlock /></button>
                      )}
                      {!t.is_platform_owner && t.status === 'suspended' && (
                        <button className="btn btn-icon btn-sm btn-success" title="Resume" onClick={() => resume(t)}><MdPlayArrow /></button>
                      )}
                      <button className="btn btn-icon btn-sm btn-outline" title="Reset their admin password" onClick={() => resetAdmin(t)}><MdLockReset /></button>
                      <button className="btn btn-icon btn-sm btn-outline" title="Download live DB backup" onClick={() => backup(t)}><MdCloudDownload /></button>
                      <button className="btn btn-icon btn-sm btn-outline" title="Snapshots & restore" onClick={() => setShowBackups(t)}><MdHistory /></button>
                      {!t.is_platform_owner && t.status !== 'archived' && (
                        <button className="btn btn-icon btn-sm btn-outline" title="Archive" onClick={() => archive(t)}><MdArchive /></button>
                      )}
                      {!t.is_platform_owner && (
                        <button className="btn btn-icon btn-sm btn-danger" title="DELETE permanently" onClick={() => hardDelete(t)}><MdDelete /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={(payload) => { setShowCreate(false); setCreateResult(payload); reload(); }}
          isSuperAdmin={isSuperAdmin}
          limits={limits}
        />
      )}
      {showPlans && (
        <PlansModal onClose={() => setShowPlans(false)} limits={limits} />
      )}
      {showBackups && (
        <BackupsModal
          // 'all' = global view (no tenant filter); else a specific tenant.
          tenant={showBackups === 'all' ? null : showBackups}
          onClose={() => setShowBackups(null)}
        />
      )}

      {editing && (
        <EditModal
          tenant={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
          isSuperAdmin={isSuperAdmin}
          limits={limits}
        />
      )}

      {createResult && (
        <CredentialsModal
          title={`Company "${createResult.tenant.name}" created`}
          subtitle="Share these credentials securely with the company admin. They will be forced to change the password on first login."
          rows={[
            ['Company slug',  createResult.tenant.slug],
            // Direct per-company link — hides the company field entirely,
            // so the admin only types username + password. This is the link
            // to share. The generic /login still works for anyone who
            // remembers their company slug.
            ['Direct login URL', `${window.location.origin}${process.env.PUBLIC_URL || ''}/${createResult.tenant.slug}/login`],
            ['Generic login URL', `${window.location.origin}${process.env.PUBLIC_URL || ''}/login`],
            ['Username',      createResult.initialAdmin?.username || '—'],
            ['Temp password', createResult.initialAdmin?.temporary_password || '—'],
          ]}
          onClose={() => setCreateResult(null)}
        />
      )}
      {resetResult && (
        <CredentialsModal
          title={`Reset admin for "${resetResult.tenant.name}"`}
          subtitle={resetResult.hint}
          rows={[
            ['Company slug',  resetResult.tenant.slug],
            ['Username',      resetResult.username],
            ['Temp password', resetResult.temporary_password],
          ]}
          onClose={() => setResetResult(null)}
        />
      )}
    </div>
  );
}

// ── Create modal ──────────────────────────────────────────────────────────
function CreateModal({ onClose, onCreated, isSuperAdmin, limits }) {
  const [form, setForm] = useState({
    slug: '', name: '',
    contact_name: '', contact_email: '', contact_phone: '',
    plan: 'standard', max_users: 25, max_assets: 10000, expires_at: '',
    admin_username: 'admin', admin_email: '', admin_full_name: 'System Administrator',
    // Optional: when SMTP isn't configured, the host admin sets the initial
    // password here and reads it back from the modal so they can deliver it
    // out-of-band (Slack/phone/etc). Blank = backend auto-generates.
    admin_password: '',
    // Super-admin only: promote the new tenant to reseller so its admins
    // can in turn onboard their own customer tenants under them.
    is_reseller: false,
  });
  const [plans, setPlans] = useState([]);  // loaded from /api/system/plans
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  // Load plan templates so the dropdown shows the configured options and
  // selecting one auto-fills the limit fields.
  useEffect(() => {
    api.get('/system/plans').then(r => setPlans(r.data || [])).catch(() => {});
  }, []);

  const applyPlan = (planKey) => {
    const tpl = plans.find(p => p.plan === planKey);
    if (!tpl) { setForm(f => ({ ...f, plan: planKey })); return; }
    // Compute expiry: today + validity_days, formatted YYYY-MM-DD for the
    // <input type="date">. Empty when validity_days is null/0 (no expiry).
    let expires = '';
    if (tpl.default_validity_days && Number(tpl.default_validity_days) > 0) {
      const d = new Date();
      d.setDate(d.getDate() + Number(tpl.default_validity_days));
      expires = d.toISOString().slice(0, 10);
    }
    setForm(f => ({
      ...f,
      plan: planKey,
      max_users:  tpl.default_max_users  ?? f.max_users,
      max_assets: tpl.default_max_assets ?? f.max_assets,
      expires_at: expires,
    }));
  };

  const submit = async () => {
    setErr('');
    if (!form.slug || !form.name) return setErr('Slug and name are required');
    const mu = Number(form.max_users) || 25;
    const ma = Number(form.max_assets) || 10000;
    if (mu > limits.maxUsers)  return setErr(`Max users cannot exceed ${limits.maxUsers.toLocaleString()}`);
    if (ma > limits.maxAssets) return setErr(`Max assets cannot exceed ${limits.maxAssets.toLocaleString()}`);
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        max_users: mu,
        max_assets: ma,
        expires_at: form.expires_at || null,
        is_reseller: isSuperAdmin && form.is_reseller ? 1 : 0,
      };
      const r = await api.post('/system/tenants', payload);
      onCreated(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || 'Create failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Modal title="Onboard a new company" onClose={onClose}>
      {err && <div className="alert alert-danger" style={{ marginBottom: 12 }}>{err}</div>}
      <Row label="Slug (lowercase, hyphens only — becomes the URL identifier)">
        <input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
               placeholder="acme-corp" className="form-control" />
      </Row>
      <Row label="Display name (shown in their UI)">
        <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
               placeholder="Acme Asset Co." className="form-control" />
      </Row>
      <Row label="Contact name">
        <input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} className="form-control" />
      </Row>
      <Row label="Contact email">
        <input value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} className="form-control" type="email" />
      </Row>
      <Row label="Contact phone">
        <input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} className="form-control" />
      </Row>
      <hr style={{ margin: '14px 0', border: 'none', borderTop: '1px dashed #ccc' }} />
      <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#555' }}>Subscription plan</h4>
      <Row label="Plan (selecting a plan auto-fills the limits below)">
        <SearchableSelect value={form.plan} onChange={e => applyPlan(e.target.value)} className="form-control">
          {plans.length === 0 && <option value="standard">Standard (default)</option>}
          {plans.map(p => (
            <option key={p.plan} value={p.plan}>
              {p.display_name || p.plan}
              {p.default_max_users != null
                ? ` — ${p.default_max_users} users · ${Number(p.default_max_assets || 0).toLocaleString()} assets${
                    p.default_validity_days ? ` · ${p.default_validity_days}d` : ''
                  }`
                : ''}
            </option>
          ))}
        </SearchableSelect>
        {plans.find(p => p.plan === form.plan)?.description && (
          <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
            {plans.find(p => p.plan === form.plan).description}
          </div>
        )}
      </Row>
      <Row label={`Max users (seats) — overrides the plan default. System ceiling: ${limits.maxUsers.toLocaleString()}.`}>
        <input type="number" min={1} max={limits.maxUsers} value={form.max_users} onChange={e => setForm({ ...form, max_users: e.target.value })} className="form-control" />
      </Row>
      <Row label={`Max assets — overrides the plan default. System ceiling: ${limits.maxAssets.toLocaleString()}.`}>
        <input type="number" min={1} max={limits.maxAssets} value={form.max_assets} onChange={e => setForm({ ...form, max_assets: e.target.value })} className="form-control" />
      </Row>
      <Row label="Subscription expiry (blank = no expiry)">
        <input type="date" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} className="form-control" />
      </Row>
      {isSuperAdmin && (
        <Row label="Onboard as reseller (their admins can create their own child companies)">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#37474f' }}>
            <input
              type="checkbox"
              checked={!!form.is_reseller}
              onChange={e => setForm({ ...form, is_reseller: e.target.checked })}
            />
            <span>Make this company a reseller</span>
          </label>
          <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
            Resellers see a Companies page that lists only the tenants they onboard themselves. They cannot edit the platform owner or other resellers, and only the platform owner can flip this flag back off.
          </div>
        </Row>
      )}
      <hr style={{ margin: '14px 0', border: 'none', borderTop: '1px dashed #ccc' }} />
      <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#555' }}>Initial admin user</h4>
      <Row label="Admin username">
        <input value={form.admin_username} onChange={e => setForm({ ...form, admin_username: e.target.value })} className="form-control" />
      </Row>
      <Row label="Admin email (where SMTP would send the temp password — optional)">
        <input value={form.admin_email} onChange={e => setForm({ ...form, admin_email: e.target.value })} className="form-control" type="email" />
      </Row>
      <Row label="Admin password (leave blank to auto-generate one — shown after creation)">
        <input
          type="text"
          value={form.admin_password}
          onChange={e => setForm({ ...form, admin_password: e.target.value })}
          placeholder="leave blank to auto-generate"
          className="form-control"
          autoComplete="new-password"
        />
        <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
          The user will be forced to change this on their first login regardless.
          Min 8 chars when set manually.
        </div>
      </Row>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button onClick={onClose} className="btn btn-outline">Cancel</button>
        <button onClick={submit} disabled={submitting} className="btn btn-primary">
          {submitting ? 'Creating...' : 'Create company'}
        </button>
      </div>
    </Modal>
  );
}

// ── Edit modal — change plan, limits, contact, expiry ────────────────────
function EditModal({ tenant, onClose, onSaved, isSuperAdmin, limits }) {
  const [form, setForm] = useState({
    name: tenant.name || '',
    contact_name:  tenant.contact_name  || '',
    contact_email: tenant.contact_email || '',
    contact_phone: tenant.contact_phone || '',
    plan:        tenant.plan || 'standard',
    max_users:   tenant.max_users || 25,
    max_assets:  tenant.max_assets || 10000,
    expires_at:  tenant.expires_at ? String(tenant.expires_at).slice(0, 10) : '',
    notes:       tenant.notes || '',
    is_reseller: !!tenant.is_reseller,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setErr('');
    const mu = Number(form.max_users);
    const ma = Number(form.max_assets);
    if (mu > limits.maxUsers)  return setErr(`Max users cannot exceed ${limits.maxUsers.toLocaleString()}`);
    if (ma > limits.maxAssets) return setErr(`Max assets cannot exceed ${limits.maxAssets.toLocaleString()}`);
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        plan: form.plan,
        max_users: mu,
        max_assets: ma,
        expires_at: form.expires_at || null,
        notes: form.notes,
        // Reseller flag: super-admin only. Skip the field for non-super-admin
        // so the backend doesn't even see an attempt to change it (it would
        // 403 anyway, but no point making the request if we know it'll fail).
        ...(isSuperAdmin && !tenant.is_platform_owner ? { is_reseller: form.is_reseller ? 1 : 0 } : {}),
      };
      await api.put(`/system/tenants/${tenant.id}`, payload);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Modal title={`Edit "${tenant.name}"`} onClose={onClose}>
      {err && <div className="alert alert-danger" style={{ marginBottom: 12 }}>{err}</div>}
      <Row label="Display name">
        <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="form-control" />
      </Row>
      <Row label="Contact name">
        <input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} className="form-control" />
      </Row>
      <Row label="Contact email">
        <input value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} className="form-control" type="email" />
      </Row>
      <Row label="Contact phone">
        <input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} className="form-control" />
      </Row>
      <Row label="Plan">
        <SearchableSelect value={form.plan} onChange={e => setForm({ ...form, plan: e.target.value })} className="form-control">
          <option value="trial">Trial</option>
          <option value="standard">Standard</option>
          <option value="enterprise">Enterprise</option>
        </SearchableSelect>
      </Row>
      <Row label={`Max users (system ceiling: ${limits.maxUsers.toLocaleString()})`}>
        <input type="number" min={1} max={limits.maxUsers} value={form.max_users} onChange={e => setForm({ ...form, max_users: e.target.value })} className="form-control" />
      </Row>
      <Row label={`Max assets (system ceiling: ${limits.maxAssets.toLocaleString()})`}>
        <input type="number" min={1} max={limits.maxAssets} value={form.max_assets} onChange={e => setForm({ ...form, max_assets: e.target.value })} className="form-control" />
      </Row>
      <Row label="Expires (blank = no expiry)">
        <input type="date" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} className="form-control" />
      </Row>
      <Row label="Notes (internal — platform owner only)">
        <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="form-control" rows={3} />
      </Row>
      {isSuperAdmin && !tenant.is_platform_owner && (
        <Row label="Reseller status">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#37474f' }}>
            <input
              type="checkbox"
              checked={!!form.is_reseller}
              onChange={e => setForm({ ...form, is_reseller: e.target.checked })}
            />
            <span>This company is a reseller (its admins can onboard their own child companies)</span>
          </label>
          <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
            Toggling this off doesn't reassign tenants they already onboarded — those keep parent_tenant_id pointing here. Reassign them on the child rows first if you want to fully unwind a reseller relationship.
          </div>
        </Row>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button onClick={onClose} className="btn btn-outline">Cancel</button>
        <button onClick={submit} disabled={saving} className="btn btn-primary">
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </Modal>
  );
}

// ── Plan templates manager ───────────────────────────────────────────────
// CRUD for plan templates. Editing a row updates the template; existing
// tenants on that plan are NOT retroactively changed (each tenant has its
// own snapshot of max_users / max_assets after onboarding). Adding /
// renaming plans is intentionally light-weight — Identium can spin up a
// "custom-bata" plan in 10 seconds and assign it via the Edit-tenant flow.
function PlansModal({ onClose, limits }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [err, setErr] = useState('');

  const reload = async () => {
    setLoading(true);
    try { const r = await api.get('/system/plans'); setPlans(r.data || []); }
    catch (e) { setErr(e.response?.data?.error || 'Failed to load plans'); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const save = async (plan, patch) => {
    setErr('');
    try { await api.put(`/system/plans/${plan}`, patch); reload(); }
    catch (e) { setErr(e.response?.data?.error || 'Save failed'); }
  };
  const remove = async (plan) => {
    if (!(await confirm(
      `Reassign any tenants on this plan first.`,
      { title: `Delete plan "${plan}"?`, confirmLabel: 'Delete', danger: true },
    ))) return;
    try { await api.delete(`/system/plans/${plan}`); reload(); }
    catch (e) { await alertBox(e.response?.data?.error || 'Delete failed', { title: 'Delete failed' }); }
  };

  return (
    <Modal title="Plan templates" onClose={onClose} width={760}>
      <p style={{ fontSize: 12, color: '#666', marginTop: 0 }}>
        Defaults applied when onboarding a new company. Editing a plan does NOT
        retroactively change existing customers — each company keeps its own
        max_users / max_assets snapshot taken at onboarding time. Use the
        Edit button on a tenant row to upgrade them after the fact.
      </p>
      {err && <div className="alert alert-danger" style={{ marginBottom: 12 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button onClick={() => setShowAdd(true)} className="btn btn-primary btn-sm"><MdAdd /> Add plan</button>
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Key</th>
              <th>Display name</th>
              <th>Max users</th>
              <th>Max assets</th>
              <th>Validity (days)</th>
              <th>Description</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 16 }}>Loading…</td></tr>
            ) : plans.map(p => <PlanRow key={p.plan} plan={p} onSave={save} onDelete={remove} limits={limits} />)}
          </tbody>
        </table>
      </div>
      {showAdd && <AddPlanModal onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); reload(); }} limits={limits} />}
      <div style={{ textAlign: 'right', marginTop: 16 }}>
        <button onClick={onClose} className="btn btn-outline">Close</button>
      </div>
    </Modal>
  );
}

function PlanRow({ plan, onSave, onDelete, limits }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(plan);
  useEffect(() => { setDraft(plan); }, [plan]);

  if (!editing) {
    return (
      <tr>
        <td><code style={{ fontSize: 12 }}>{plan.plan}</code></td>
        <td style={{ fontWeight: 600 }}>{plan.display_name}</td>
        <td>{Number(plan.default_max_users).toLocaleString()}</td>
        <td>{Number(plan.default_max_assets).toLocaleString()}</td>
        <td>{plan.default_validity_days || <span style={{ color: '#999' }}>no expiry</span>}</td>
        <td style={{ fontSize: 12, color: '#666', maxWidth: 240 }}>{plan.description || '—'}</td>
        <td style={{ display: 'flex', gap: 4 }}>
          <button className="btn btn-icon btn-sm btn-outline" onClick={() => setEditing(true)} title="Edit"><MdEdit /></button>
          <button className="btn btn-icon btn-sm btn-danger" onClick={() => onDelete(plan.plan)} title="Delete"><MdDelete /></button>
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td><code style={{ fontSize: 12 }}>{plan.plan}</code></td>
      <td><input value={draft.display_name || ''} onChange={e => setDraft({ ...draft, display_name: e.target.value })} className="form-control form-control-sm" /></td>
      <td><input type="number" min={1} max={limits?.maxUsers}  value={draft.default_max_users || ''} onChange={e => setDraft({ ...draft, default_max_users: e.target.value })} className="form-control form-control-sm" style={{ width: 90 }} /></td>
      <td><input type="number" min={1} max={limits?.maxAssets} value={draft.default_max_assets || ''} onChange={e => setDraft({ ...draft, default_max_assets: e.target.value })} className="form-control form-control-sm" style={{ width: 110 }} /></td>
      <td><input type="number" min={1} value={draft.default_validity_days ?? ''} placeholder="—" onChange={e => setDraft({ ...draft, default_validity_days: e.target.value === '' ? null : e.target.value })} className="form-control form-control-sm" style={{ width: 80 }} /></td>
      <td><input value={draft.description || ''} onChange={e => setDraft({ ...draft, description: e.target.value })} className="form-control form-control-sm" /></td>
      <td style={{ display: 'flex', gap: 4 }}>
        <button className="btn btn-icon btn-sm btn-success" onClick={() => { onSave(plan.plan, draft); setEditing(false); }} title="Save"><MdSave /></button>
        <button className="btn btn-icon btn-sm btn-outline" onClick={() => { setDraft(plan); setEditing(false); }} title="Cancel"><MdClose /></button>
      </td>
    </tr>
  );
}

function AddPlanModal({ onClose, onAdded, limits }) {
  const [f, setF] = useState({
    plan: '', display_name: '',
    default_max_users: 25, default_max_assets: 10000,
    default_validity_days: '', description: '',
  });
  const [err, setErr] = useState('');
  const submit = async () => {
    setErr('');
    try {
      await api.post('/system/plans', {
        ...f,
        default_max_users:  Number(f.default_max_users),
        default_max_assets: Number(f.default_max_assets),
        default_validity_days: f.default_validity_days === '' ? null : Number(f.default_validity_days),
      });
      onAdded();
    } catch (e) { setErr(e.response?.data?.error || 'Add failed'); }
  };
  return (
    <Modal title="Add plan template" onClose={onClose} width={460}>
      {err && <div className="alert alert-danger" style={{ marginBottom: 12 }}>{err}</div>}
      <Row label="Key (a-z, 0-9, hyphens; e.g. starter, enterprise-plus)">
        <input value={f.plan} onChange={e => setF({ ...f, plan: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
               placeholder="starter" className="form-control" />
      </Row>
      <Row label="Display name"><input value={f.display_name} onChange={e => setF({ ...f, display_name: e.target.value })} placeholder="Starter" className="form-control" /></Row>
      <Row label={`Max users (seats) — system ceiling: ${limits?.maxUsers?.toLocaleString?.() || '100,000'}`}><input type="number" min={1} max={limits?.maxUsers} value={f.default_max_users} onChange={e => setF({ ...f, default_max_users: e.target.value })} className="form-control" /></Row>
      <Row label={`Max assets — system ceiling: ${limits?.maxAssets?.toLocaleString?.() || '100,000,000'}`}><input type="number" min={1} max={limits?.maxAssets} value={f.default_max_assets} onChange={e => setF({ ...f, default_max_assets: e.target.value })} className="form-control" /></Row>
      <Row label="Validity in days (blank = no expiry)"><input type="number" min={1} value={f.default_validity_days} onChange={e => setF({ ...f, default_validity_days: e.target.value })} className="form-control" /></Row>
      <Row label="Description (shown to host admin in the plan picker)"><input value={f.description} onChange={e => setF({ ...f, description: e.target.value })} className="form-control" /></Row>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button onClick={onClose} className="btn btn-outline">Cancel</button>
        <button onClick={submit} className="btn btn-primary">Add</button>
      </div>
    </Modal>
  );
}

function CredentialsModal({ title, subtitle, rows, onClose }) {
  const copy = (val) => navigator.clipboard?.writeText(val);
  return (
    <Modal title={title} onClose={onClose} width={520}>
      <p style={{ fontSize: 13, color: '#555', marginTop: 0 }}>{subtitle}</p>
      <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: 12 }}>
        {rows.map(([label, val]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
            <div style={{ width: 130, fontSize: 12, color: '#7a5400', fontWeight: 600 }}>{label}</div>
            <code style={{ flex: 1, fontFamily: 'monospace', fontSize: 13, background: 'white', padding: '4px 10px', borderRadius: 6 }}>{val}</code>
            <button onClick={() => copy(val)} className="btn btn-icon btn-sm btn-outline" title="Copy"><MdContentCopy /></button>
          </div>
        ))}
      </div>
      <div style={{ textAlign: 'right', marginTop: 16 }}>
        <button onClick={onClose} className="btn btn-primary">Done</button>
      </div>
    </Modal>
  );
}

function Modal({ title, children, onClose, width = 520 }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'white', borderRadius: 14, padding: 20, width: '100%', maxWidth: width,
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#546e7a', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

// ── Backups & restore modal ──────────────────────────────────────────────
// Two modes: tenant-scoped (shows snapshots for one company + restore
// affordances) vs global (the whole snapshot inventory + scheduler status).
// Restore is intentionally not casual — typing the slug is required and a
// pre-restore safety snapshot is taken on the backend before the swap.
function BackupsModal({ tenant, onClose }) {
  const [status, setStatus]     = useState(null);   // scheduler status
  const [snaps, setSnaps]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [busy, setBusy]         = useState(false);  // run-now / restore in flight
  const [err, setErr]           = useState('');
  const [info, setInfo]         = useState('');

  const isPlatformOwner = !!tenant?.is_platform_owner;

  const reload = async () => {
    setLoading(true); setErr('');
    try {
      const [s, l] = await Promise.all([
        api.get('/system/backups/status'),
        api.get('/system/backups/list', { params: tenant ? { slug: tenant.slug } : {} }),
      ]);
      setStatus(s.data);
      setSnaps(l.data || []);
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to load backups');
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [tenant?.id]);

  const runNow = async () => {
    if (!(await confirm(
      'Trigger a backup of every active company right now? This is normally automatic.',
      { title: 'Run backups now', confirmLabel: 'Run' },
    ))) return;
    setBusy(true); setErr(''); setInfo('');
    try {
      const r = await api.post('/system/backups/run-now');
      setInfo(`Snapshot complete: ${r.data.summary.ok}/${r.data.summary.total} succeeded.`);
      await reload();
    } catch (e) {
      setErr(e.response?.data?.error || 'Run failed');
    } finally { setBusy(false); }
  };

  const downloadSnap = (s) => {
    // Use a token query param so the download fires from a normal anchor
    // click without needing to construct an authed fetch + blob path.
    const token = localStorage.getItem('asset_token') || localStorage.getItem('identium_host_token');
    const url = `${api.defaults.baseURL}/system/backups/${s.slug}/${s.date}/download?token=${encodeURIComponent(token || '')}`;
    // Fall back to authed blob fetch since most installs gate /api on the
    // Authorization header rather than a token param.
    api.get(`/system/backups/${s.slug}/${s.date}/download`, { responseType: 'blob' })
      .then(r => {
        const blobUrl = window.URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement('a');
        a.href = blobUrl; a.download = `${s.slug}-${s.date}.db`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30_000);
      })
      .catch(() => window.open(url, '_blank'));
  };

  const restoreFromSnap = async (s) => {
    if (!tenant || isPlatformOwner) return;
    const typed = await prompt(
      `This OVERWRITES the live database for "${tenant.name}" with the snapshot dated ${s.date}. A pre-restore safety copy will be taken first. All users in this company will be logged out.`,
      '',
      { title: 'Confirm restore', inputLabel: `Type "${tenant.slug}" to confirm`,
        placeholder: tenant.slug, confirmLabel: 'Restore' },
    );
    if (typed !== tenant.slug) return;
    setBusy(true); setErr(''); setInfo('');
    try {
      const r = await api.post(`/system/tenants/${tenant.id}/restore`, { date: s.date });
      setInfo(r.data.message || 'Restore complete.');
      await reload();
    } catch (e) {
      setErr(e.response?.data?.error || 'Restore failed');
    } finally { setBusy(false); }
  };

  const restoreFromUpload = async (file) => {
    if (!tenant || isPlatformOwner || !file) return;
    const typed = await prompt(
      `This OVERWRITES the live database for "${tenant.name}" with the uploaded file "${file.name}". A pre-restore safety copy will be taken first. All users in this company will be logged out.`,
      '',
      { title: 'Confirm restore', inputLabel: `Type "${tenant.slug}" to confirm`,
        placeholder: tenant.slug, confirmLabel: 'Restore' },
    );
    if (typed !== tenant.slug) return;
    setBusy(true); setErr(''); setInfo('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/system/tenants/${tenant.id}/restore`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setInfo(r.data.message || 'Restore complete.');
      await reload();
    } catch (e) {
      setErr(e.response?.data?.error || 'Restore failed');
    } finally { setBusy(false); }
  };

  const fmtBytes = (n) => {
    if (n == null) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
  };

  return (
    <Modal
      title={tenant ? `Backups — ${tenant.name}` : 'Backups (all companies)'}
      onClose={onClose}
      width={680}
    >
      {err  && <div className="alert alert-danger"  style={{ marginBottom: 12 }}>{err}</div>}
      {info && <div className="alert alert-success" style={{ marginBottom: 12 }}>{info}</div>}

      {/* Scheduler status */}
      <div style={{
        background: '#f8fafc', border: '1px solid #e0e6ef', borderRadius: 10,
        padding: 14, marginBottom: 14, display: 'flex', gap: 16, flexWrap: 'wrap',
        alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ fontSize: 13, color: '#37474f' }}>
          <div><strong>Schedule:</strong> daily at {status?.scheduleLocal || '—'} (local)</div>
          <div><strong>Retention:</strong> {status?.retentionDays ?? '—'} days</div>
          <div><strong>Last run:</strong> {status?.lastRunAt ? new Date(status.lastRunAt).toLocaleString() : 'never'} {status?.lastSummary && (
            <span style={{ color: status.lastSummary.failed ? '#c62828' : '#2e7d32', marginLeft: 6 }}>
              ({status.lastSummary.ok}/{status.lastSummary.total} ok)
            </span>
          )}</div>
        </div>
        <button onClick={runNow} disabled={busy} className="btn btn-outline btn-sm">
          <MdBackup /> Run now
        </button>
      </div>

      {/* Restore-from-upload — only when scoped to a tenant and not the
          platform-owner. Upload-based restore is the only path when the
          snapshot you want isn't on this server (e.g. restoring from an
          off-site backup the operator pulled in via rsync/S3). */}
      {tenant && !isPlatformOwner && (
        <div style={{
          background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: 10,
          padding: 12, marginBottom: 14, fontSize: 13,
        }}>
          <div style={{ fontWeight: 600, color: '#e65100', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <MdRestore /> Restore from external file
          </div>
          <div style={{ color: '#666', marginBottom: 8 }}>
            Upload a .db file (e.g. from off-site storage). Replaces the live database for this company.
          </div>
          <label className="btn btn-outline btn-sm" style={{ cursor: 'pointer' }}>
            <MdUploadFile /> Choose .db file
            <input
              type="file" accept=".db,application/octet-stream" style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) restoreFromUpload(f); }}
              disabled={busy}
            />
          </label>
        </div>
      )}

      {tenant && isPlatformOwner && (
        <div className="alert alert-warning" style={{ marginBottom: 12, fontSize: 13 }}>
          The platform-owner tenant can't be restored through this UI. If you need to restore the platform owner's own DB, replace the file directly on disk and restart the server.
        </div>
      )}

      {/* Snapshot list */}
      <div style={{ fontWeight: 600, fontSize: 13, color: '#546e7a', marginBottom: 8 }}>
        Snapshots {tenant ? `for ${tenant.slug}` : 'across all companies'} ({snaps.length})
      </div>
      <div className="table-container" style={{ maxHeight: 320, overflowY: 'auto' }}>
        <table>
          <thead>
            <tr>
              {!tenant && <th>Company</th>}
              <th>Date</th>
              <th>Size</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={tenant ? 3 : 4} style={{ textAlign: 'center', padding: 20 }}>Loading…</td></tr>
            ) : snaps.length === 0 ? (
              <tr><td colSpan={tenant ? 3 : 4} style={{ textAlign: 'center', padding: 20, color: '#888' }}>
                No snapshots yet. Click "Run now" or wait for the next scheduled run.
              </td></tr>
            ) : snaps.map(s => (
              <tr key={`${s.slug}-${s.date}`}>
                {!tenant && <td><code style={{ fontSize: 12 }}>{s.slug}</code></td>}
                <td>{s.date}</td>
                <td style={{ fontSize: 12, color: '#666' }}>{fmtBytes(s.bytes)}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn btn-icon btn-sm btn-outline" title="Download" onClick={() => downloadSnap(s)}>
                    <MdCloudDownload />
                  </button>
                  {tenant && !isPlatformOwner && (
                    <button
                      className="btn btn-icon btn-sm btn-danger"
                      title="Restore from this snapshot"
                      onClick={() => restoreFromSnap(s)}
                      disabled={busy}
                      style={{ marginLeft: 4 }}
                    >
                      <MdRestore />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ textAlign: 'right', marginTop: 16 }}>
        <button onClick={onClose} className="btn btn-primary">Done</button>
      </div>
    </Modal>
  );
}
